import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { authService, LoginResponse } from "@/services/authService";
import { LocalStorageKeys, useLocalStorage } from "./hooks/useLocalStorage";
import { Loader } from "lucide-react";
import { useServices } from "@/hooks/useServices";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useQueryClient } from "@tanstack/react-query";
import {
  decodeJwtPayload,
  getHotelIdFromJwtPayload,
} from "@/utils/authSessionUser";

const AuthCallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const {
    setAccessToken,
    setRefreshToken,
    setTenantId,
    setHotelId,
    setAdminAccessToken,
    setAdminRefreshToken,
  } = useLocalStorage();
  const { setAuthToken } = useServices();
  const queryClient = useQueryClient();

  useEffect(() => {
    const run = async () => {
      const errorParam = searchParams.get("error");
      if (errorParam) {
        setError(errorParam);
        setIsLoading(false);
        return;
      }

      const code = searchParams.get("code");
      const state = searchParams.get("state");
      const iss = searchParams.get("iss");

      if (!code) {
        setError("Missing authorization code");
        setIsLoading(false);
        return;
      }

      if (!iss) {
        setError("Missing issuer");
        setIsLoading(false);
        return;
      }

      try {
        const redirectUri = `${window.location.origin}/auth/callback`;
        const response: LoginResponse = await authService.exchangeCode({
          code,
          state,
          redirectUri,
          iss,
        });

        setAccessToken(response.accessToken);
        setRefreshToken(response.refreshToken);
        setTenantId(response.user.tenantId);
        setHotelId(
          getHotelIdFromJwtPayload(
            decodeJwtPayload(response.accessToken) ?? {},
          ),
        );
        setAdminAccessToken(null);
        setAdminRefreshToken(null);
        localStorage.removeItem(LocalStorageKeys.ADMIN_TENANT_ID);
        localStorage.removeItem(LocalStorageKeys.ADMIN_HOTEL_ID);

        setAuthToken(response.accessToken);

        await queryClient.invalidateQueries({ queryKey: ["userProfile"] });

        navigate("/", { replace: true });
      } catch (e: any) {
        console.error("Auth callback failed", e);
        setError(e?.message || "Authentication failed");
      } finally {
        setIsLoading(false);
      }
    };

    run();
  }, [
    navigate,
    queryClient,
    searchParams,
    setAccessToken,
    setAdminAccessToken,
    setAdminRefreshToken,
    setAuthToken,
    setHotelId,
    setRefreshToken,
    setTenantId,
  ]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          <Card>
            <CardHeader>
              <CardTitle className="text-center">Signing you in</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-gray-700 flex items-center justify-center gap-2">
                <Loader className="animate-spin" />
                Completing sign-in...
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          <Card className="border-red-200">
            <CardHeader>
              <CardTitle className="text-red-600 text-center">
                Authentication Error
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 text-center">{error}</p>
            </CardContent>
            <CardFooter className="flex justify-center">
              <Button onClick={() => navigate("/login", { replace: true })}>
                Back to Login
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    );
  }

  return null;
};

export default AuthCallbackPage;
